import { useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import { PlanState } from '@lib/enums/plan';

// The slice of the host app's dashboard outlet context this hook reads —
// admin and user both provide it.
type ContextProps = {
  getUserAccessByCompany: (component: AccessComponentEnum) => AccessLevelEnum;
  handleOpenPlanModal: (
    state: PlanState,
    features?: CompanyPlanUpgradeFeatureEnum[],
  ) => void;
  isFullAdmin: boolean;
};

/**
 * Shared wiring for the Document response cells rendered by the completion
 * detail and compare tables: whether EDM is locked for this company, how to
 * open the plan modal for it, and how to open a document in a new tab.
 * `DocumentResponseCell` handles the locked-state UI per role, so callers
 * render Document rows either way and just forward these.
 */
export default function useDocumentationCellAccess(
  getDocumentationUrl: (documentationId: string) => string,
) {
  const {
    getUserAccessByCompany,
    handleOpenPlanModal,
    isFullAdmin,
  }: ContextProps = useOutletContext();

  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const onUpgradePlan = useCallback(() => {
    handleOpenPlanModal(PlanState.Locked, [
      CompanyPlanUpgradeFeatureEnum.Documentation,
    ]);
  }, [handleOpenPlanModal]);

  const onDocumentClick = useCallback(
    (documentationId: string) => {
      window.open(getDocumentationUrl(documentationId), '_blank');
    },
    [getDocumentationUrl],
  );

  return { isViewLocked, isFullAdmin, onUpgradePlan, onDocumentClick };
}
